/* =========================================================
   ZEUS MEGA MENU V7 — overlay + mobile accordion behaviors
   - Open/close delegated on [aria-controls="zeus-mega-menu"] (survives header re-render)
   - Toggle: a second click on the trigger closes; Escape closes; focus restored
     to the live trigger (WCAG 2.1.2 / 2.4.3)
   - Focus trap: visibleFocusables() recomputed on every Tab (includes <summary>)
   - --zeus-mm-top: panel/drawer open BELOW the sticky header (Paper boards 19/22)
   - Scroll lock + ZeusOverlayLock takeover coordination
   - Accordion: single-open pattern; rebound on shopify:section:load
   - Respects prefers-reduced-motion per AGENTS 3.4
   - Standalone vanilla JS — NOT coupled to theme.js / navigation-drawer.
   ========================================================= */
(function () {
  "use strict";

  var OVERLAY_ID = "zeus-mega-menu";
  // Include native <summary> so accordion headers participate in the focus trap.
  var FOCUSABLE =
    'a[href], button:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';
  var MOBILE_QUERY = "(max-width: 999.98px)";
  var lastTrigger = null;
  var accordions = [];

  /* ---------- helpers ---------- */

  function overlay() {
    return document.getElementById(OVERLAY_ID);
  }

  function isVisible(node) {
    return !!(node && node.isConnected && node.getClientRects().length);
  }

  function triggers() {
    return document.querySelectorAll('[aria-controls="' + OVERLAY_ID + '"]');
  }

  function syncTriggerState(expanded) {
    Array.prototype.forEach.call(triggers(), function (trigger) {
      trigger.setAttribute("aria-expanded", expanded ? "true" : "false");
    });
  }

  function anotherOverlayOpen() {
    return !!document.querySelector(".drawer[open]");
  }

  function lockScroll() {
    document.documentElement.classList.add("lock");
  }

  function unlockScroll() {
    if (!anotherOverlayOpen()) {
      document.documentElement.classList.remove("lock");
    }
  }

  function syncOverlayCoordinator() {
    if (window.ZeusOverlayLock && typeof window.ZeusOverlayLock.sync === "function") {
      window.ZeusOverlayLock.sync();
    }
  }

  function isMobile() {
    return window.matchMedia(MOBILE_QUERY).matches;
  }

  function activeRegion(root) {
    return root.querySelector(isMobile() ? ".mm-drawer" : ".mm-panel");
  }

  function visibleFocusables(node) {
    if (!node) return [];
    return Array.prototype.filter.call(node.querySelectorAll(FOCUSABLE), function (el) {
      return isVisible(el) && !el.closest("[hidden], [inert]");
    });
  }

  // Paper boards 19/22: panel opens below the sticky header (announce bar + header stay
  // visible, trigger stays clickable -> second click closes).
  function syncHeaderOffset(root) {
    var header = document.querySelector(".header");
    var bottom = header ? Math.max(0, Math.round(header.getBoundingClientRect().bottom)) : 0;
    root.style.setProperty("--zeus-mm-top", bottom + "px");
  }

  /* ---------- overlay open / close ---------- */

  function openOverlay(trigger) {
    var root = overlay();
    if (!root || root.classList.contains("is-open")) return;
    lastTrigger = trigger || document.querySelector('[aria-controls="' + OVERLAY_ID + '"]');
    syncHeaderOffset(root);
    root.classList.add("is-open");
    syncTriggerState(true);
    lockScroll();
    syncOverlayCoordinator();
    var region = activeRegion(root);
    if (region) region.classList.add("is-open");
    var focusables = visibleFocusables(region);
    var first = focusables[0] || region || root;
    if (first && first.focus) first.focus();
  }

  function closeOverlay(restoreFocus) {
    var root = overlay();
    if (!root || !root.classList.contains("is-open")) return;
    var region = activeRegion(root);
    if (region) region.classList.remove("is-open");
    root.classList.remove("is-open");
    syncTriggerState(false);
    unlockScroll();
    syncOverlayCoordinator();
    if (restoreFocus === false) return;
    // Resolve the trigger at use time: the cached node may be detached after a header re-render.
    var target = isVisible(lastTrigger) ? lastTrigger : null;
    if (!target) {
      var list = triggers();
      for (var i = 0; i < list.length; i++) {
        if (isVisible(list[i])) {
          target = list[i];
          break;
        }
      }
    }
    if (target && target.focus) target.focus();
  }

  function bindDocumentListeners() {
    document.addEventListener("click", function (e) {
      var opener = e.target.closest('[aria-controls="' + OVERLAY_ID + '"]');
      var root = overlay();
      if (opener) {
        e.preventDefault();
        if (root && root.classList.contains("is-open")) closeOverlay();
        else openOverlay(opener);
        return;
      }
      if (!root || !root.classList.contains("is-open")) return;
      // Click on the exposed surface (outside panel/drawer) closes.
      if (!e.target.closest(".mm-panel, .mm-drawer")) closeOverlay();
    });

    document.addEventListener("keydown", function (e) {
      var root = overlay();
      if (!root || !root.classList.contains("is-open")) return;

      if (e.key === "Escape") {
        e.preventDefault();
        closeOverlay();
        return;
      }

      if (e.key !== "Tab") return;
      var region = activeRegion(root);
      var focusables = visibleFocusables(region);
      if (!focusables.length) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      var inside = region.contains(document.activeElement);
      if (e.shiftKey && (!inside || document.activeElement === first)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    });

    window.addEventListener("resize", function () {
      var root = overlay();
      if (!root || !root.classList.contains("is-open")) return;
      syncHeaderOffset(root);
      // Breakpoint crossed while open: move open state to the visible region.
      var region = activeRegion(root);
      root.querySelectorAll(".mm-panel, .mm-drawer").forEach(function (node) {
        node.classList.toggle("is-open", node === region);
      });
    });

    document.addEventListener("shopify:section:load", function (event) {
      if (!event.target || !event.target.querySelector) return;
      if (!event.target.querySelector("#" + OVERLAY_ID)) return;
      bindAccordions(event.target);
    });
  }

  /* ---------- mobile accordions ---------- */

  function bindAccordions(scope) {
    var root = scope && scope.querySelectorAll ? scope : document;
    var nodes = root.querySelectorAll("[data-mm-accordion]");
    // Rebuild the navigable list from the live DOM (section reloads replace nodes).
    accordions = Array.prototype.slice.call(document.querySelectorAll("[data-mm-accordion]"));

    nodes.forEach(function (node) {
      if (node.dataset.mmBound === "1") return;
      node.dataset.mmBound = "1";

      var summary = node.querySelector(".mm-accordion__summary");
      if (!summary) return;

      // Real <details> state is the single source of truth: sync the visual class
      // (tint + chevron + burnt label) and enforce single-open on every toggle.
      node.addEventListener("toggle", function () {
        node.classList.toggle("mm-accordion--open", node.open);
        if (node.open) {
          closeOthers(node);
        }
      });

      // Keyboard: Enter/Space on summary toggles (native), Arrow keys navigate
      summary.addEventListener("keydown", function (e) {
        if (e.key === "ArrowDown" || e.key === "ArrowUp") {
          e.preventDefault();
          focusAdjacent(e.key === "ArrowDown" ? 1 : -1, node);
        }
      });
    });

    // Open first accordion by default (Niveau) if none have [open]
    var hasOpen = accordions.some(function (a) {
      return a.open;
    });
    if (!hasOpen && accordions.length) {
      var defaultOpen = document.querySelector("[data-mm-accordion].mm-accordion--open");
      if (defaultOpen) {
        defaultOpen.open = true;
      }
    }
  }

  function closeOthers(current) {
    accordions.forEach(function (node) {
      if (node !== current && node.open) {
        node.open = false;
      }
    });
  }

  function focusAdjacent(dir, current) {
    var idx = accordions.indexOf(current);
    if (idx === -1) return;
    var nextIdx = idx + dir;
    if (nextIdx < 0) nextIdx = accordions.length - 1;
    if (nextIdx >= accordions.length) nextIdx = 0;
    var target = accordions[nextIdx].querySelector(".mm-accordion__summary");
    if (target) target.focus();
  }

  function boot() {
    bindAccordions(document);
  }

  // Idempotency: section re-render can re-execute this script; keep document
  // listeners once, and rebind accordions via shopify:section:load.
  if (!window.__zeusMegaMenuBound) {
    window.__zeusMegaMenuBound = true;
    bindDocumentListeners();

    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", boot);
    } else {
      boot();
    }
  } else {
    // Script re-ran after a section replace before section:load — rebind now.
    bindAccordions(document);
  }
})();
